import { convertToCommonErrors, convertToCommonErrorsUsingErrorsMessages } from '../../common/utils/errorUtils.js';
import { TypeKeys, generateActionId } from './actionsUtils.js';
import type { CrudAction, ErrorAction } from './actionsUtils.js';
import type { AddressSearchResult } from '../../components/AddressSearch/AddressSearch.js';
import type { GenericError, NumberRangeResponse, PageSearchResponse } from '../../generated/api/models.js';
import type { Action } from 'redux';

export interface LoadNumberRangeAction extends CrudAction<TypeKeys.LOAD_NUMBER_RANGE> {
  rangeId: string;
}

export const loadNumberRange = (rangeId: string): LoadNumberRangeAction => ({
  id: generateActionId(),
  rangeId,
  type: TypeKeys.LOAD_NUMBER_RANGE,
});

export interface LoadNumberRangeFailedAction extends ErrorAction<TypeKeys.LOAD_NUMBER_RANGE_FAILED> {
  rangeId: string;
}

export const loadNumberRangeFailed = (
  rangeId: string,
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): LoadNumberRangeFailedAction => ({
  errors: convertToCommonErrorsUsingErrorsMessages(message, status, errors),
  params,
  rangeId,
  type: TypeKeys.LOAD_NUMBER_RANGE_FAILED,
});

export interface LoadNumberRangeFulfilledAction extends Action<TypeKeys.LOAD_NUMBER_RANGE_FULFILLED> {
  numberCategory: NumberRangeResponse.NumberCategoryEnum;
  numbers: string[];
  rangeId: string;
}

export const loadNumberRangeFulfilled = (
  rangeId: string,
  numberCategory: NumberRangeResponse.NumberCategoryEnum,
  numbers: string[]
): LoadNumberRangeFulfilledAction => ({
  numberCategory,
  numbers,
  rangeId,
  type: TypeKeys.LOAD_NUMBER_RANGE_FULFILLED,
});

export interface ResetAddressSearchResultAction extends Action {
  type: TypeKeys.RESET_ADDRESS_SEARCH_RESULT;
}

export const resetAddressSearchResult = (): ResetAddressSearchResultAction => ({
  type: TypeKeys.RESET_ADDRESS_SEARCH_RESULT,
});

export interface SearchAddressAction extends CrudAction<TypeKeys.SEARCH_ADDRESS> {
  query: string;
  postalCode: string;
}

export const searchAddress = (query: string, postalCode: string): SearchAddressAction => ({
  id: generateActionId(),
  query: query,
  postalCode: postalCode,
  type: TypeKeys.SEARCH_ADDRESS,
});

export type SearchAddressFailedAction = ErrorAction<TypeKeys.SEARCH_ADDRESS_FAILED>;

export const searchAddressFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): SearchAddressFailedAction => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.SEARCH_ADDRESS_FAILED,
});

export interface SearchAddressFulfilledAction extends Action {
  result: AddressSearchResult;
  type: TypeKeys.SEARCH_ADDRESS_FULFILLED;
}

export const searchAddressFulfilled = (result: AddressSearchResult): SearchAddressFulfilledAction => ({
  result: result,
  type: TypeKeys.SEARCH_ADDRESS_FULFILLED,
});

export interface ResetSearchPublicPagesResultAction extends Action {
  type: TypeKeys.RESET_SEARCH_PUBLIC_PAGES_RESULT;
}

export const resetSearchPagesSearchResult = (): ResetSearchPublicPagesResultAction => ({
  type: TypeKeys.RESET_SEARCH_PUBLIC_PAGES_RESULT,
});

export interface SearchPublicPagesAction extends CrudAction<TypeKeys.SEARCH_PUBLIC_PAGES> {
  query: string;
  start: number;
}

export const searchPage = (query: string, start = 1): SearchPublicPagesAction => ({
  id: generateActionId(),
  query: query,
  start: start,
  type: TypeKeys.SEARCH_PUBLIC_PAGES,
});

export type SearchPublicPagesFailedAction = ErrorAction<TypeKeys.SEARCH_PUBLIC_PAGES_FAILED>;

export const searchPublicPagesFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): SearchPublicPagesFailedAction => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.SEARCH_PUBLIC_PAGES_FAILED,
});

export interface SearchPublicPagesFulfilledAction extends Action {
  query: string;
  start: number;
  result: PageSearchResponse;
  type: TypeKeys.SEARCH_PUBLIC_PAGES_FULFILLED;
}

export const searchPublicPagesFulfilled = (
  query: string,
  start: number,
  result: PageSearchResponse
): SearchPublicPagesFulfilledAction => ({
  query,
  start,
  result,
  type: TypeKeys.SEARCH_PUBLIC_PAGES_FULFILLED,
});

export type ResourcesActionTypes =
  | LoadNumberRangeAction
  | LoadNumberRangeFailedAction
  | LoadNumberRangeFulfilledAction
  | ResetAddressSearchResultAction
  | SearchAddressAction
  | SearchAddressFailedAction
  | SearchAddressFulfilledAction
  | ResetSearchPublicPagesResultAction
  | SearchPublicPagesAction
  | SearchPublicPagesFailedAction
  | SearchPublicPagesFulfilledAction;
